/**
 * Cart Drawer (Ajax)
 * - Smooth qty updates + stock clamp (422)
 * - Drawer skeleton via [data-cart-drawer-loading]
 * - Free shipping progress (UK-only, shows even when cart is empty)
 * - Stock / limit messages show INLINE per line item with REAL fade ✅
 * - ✅ FIX: prevent “flash/duplicate lines” by hiding content during skeleton refresh
 * - ✅ FIX: on 422, DO NOT refresh (cart didn’t change) + unlock row so it doesn’t stay disabled
 */
(function () {
  if (window.__CART_DRAWER_INIT__) return;
  window.__CART_DRAWER_INIT__ = true;

  window.CartDrawerVersion = 'drawer-smooth-v12-422-unlock-row';

  const selectors = {
    drawer: '[data-cart-drawer]',
    overlay: '[data-cart-drawer-overlay]',
    closeBtn: '[data-cart-drawer-close]',
    loading: '[data-cart-drawer-loading]',
    content: '[data-cart-drawer-content]',
    subtotal: '[data-cart-drawer-subtotal]',
    error: '[data-cart-drawer-error]',
    live: '[data-cart-drawer-live]',
    openers: '[data-cart-drawer-open]',
    headerCount: '[data-cart-count]',

    freeWrap: '[data-free-shipping]',
    freeText: '[data-free-shipping-text]',
    freeFill: '[data-free-shipping-fill]',

    item: '[data-cart-item]',
    itemError: '[data-item-error]'
  };

  const moneyFormat =
    (window.Shopify && Shopify.money_format) ? Shopify.money_format : '£{{amount}}';

  function formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    return moneyFormat.replace('{{amount}}', value).replace('{{ amount }}', value);
  }

  function escapeHTML(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  /* Product titles carry a back-office season code after the gender, e.g.
     "Mens AW26 Dubarry Switzer Ankle Boots". Liquid strips it from the tag
     elsewhere, but /cart.js returns the raw title with no tags attached, so
     the drawer matches the token itself: SS or AW followed by two digits,
     as a whole word. */
  function cleanTitle(str) {
    return String(str)
      .replace(/\b(?:SS|AW)\d{2}\b/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  async function fetchJSON(url, options) {
    const res = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      ...options
    });

    if (res.ok) return res.json();

    let payload = null;
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) payload = await res.json();
    } catch (e) {}

    const err = new Error(`Request failed: ${res.status}`);
    err.status = res.status;
    err.payload = payload;
    const description = payload && (payload.description || payload.message);
    if (description) err.userMessage = String(description);
    return Promise.reject(err);
  }

  async function getCart() {
    return fetchJSON('/cart.js', { method: 'GET' });
  }

  async function changeLineByKey(key, quantity) {
    const res = await fetchJSON('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity })
    });

    // If changes were made while on /cart, refresh the cart page ONCE after drawer updates
    if (window.location.pathname === '/cart') {
      sessionStorage.setItem('cart_drawer_needs_cart_refresh', '1');
    }

    return res;
  }

  async function addToCart(payload) {
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  // Inventory lookup via variant endpoint (cache)
  const variantInventoryCache = new Map();
  const variantInventoryInflight = new Map();

  async function fetchVariantInventory(variantId) {
    const id = Number(variantId);
    if (!id) return null;

    if (variantInventoryCache.has(id)) return variantInventoryCache.get(id);
    if (variantInventoryInflight.has(id)) return variantInventoryInflight.get(id);

    const p = (async () => {
      try {
        const res = await fetch(`/variants/${id}.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) throw new Error('Variant fetch failed');
        const data = await res.json();
        
        // Return full variant data including inventory, price, and compare_at_price
        const variantData = {
          inventory_quantity: (data && typeof data.inventory_quantity === 'number') ? data.inventory_quantity : null,
          price: data?.price || null,
          compare_at_price: data?.compare_at_price || null
        };
        
        variantInventoryCache.set(id, variantData);
        return variantData;
      } catch (e) {
        variantInventoryCache.set(id, null);
        return null;
      } finally {
        variantInventoryInflight.delete(id);
      }
    })();

    variantInventoryInflight.set(id, p);
    return p;
  }

  // Product data cache (for tags like final_sale)
  const productCache = new Map();
  const productInflight = new Map();

  async function fetchProductData(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);
    if (productInflight.has(handle)) return productInflight.get(handle);

    const p = (async () => {
      try {
        const res = await fetch(`/products/${handle}.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) throw new Error('Product fetch failed');
        const data = await res.json();
        productCache.set(handle, data);
        return data;
      } catch (e) {
        productCache.set(handle, null);
        return null;
      } finally {
        productInflight.delete(handle);
      }
    })();

    productInflight.set(handle, p);
    return p;
  }

  function variantText(item) {
    if (item.variant_title && item.variant_title !== 'Default Title') return item.variant_title;
    return '';
  }

  function stockMessageFromMax(max) {
    if (typeof max === 'number') {
      if (max <= 0) return 'Sorry, this item is sold out.';
      if (max === 1) return 'Only 1 available in stock.';
      return `Only ${max} available in stock.`;
    }
    return null;
  }

  function stockMessageFromShopify422(err) {
    if (err && err.userMessage) return err.userMessage;
    return 'Sorry, we don’t have that many available right now.';
  }

  function renderItems(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return `
        <div class="cart-drawer__empty">
          <p class="cart-drawer__empty-text">Your bag is currently empty.</p>
          <button type="button" class="cart-drawer__empty-button" data-close-cart>
            Continue shopping
          </button>

          <div data-empty-recs></div>
        </div>
      `;
    }

    return `
      <div class="cart-drawer__items">
        ${cart.items.map((item) => {
          const title = escapeHTML(cleanTitle(item.product_title || item.title));
          const variant = escapeHTML(variantText(item));
          const img = item.image
            ? `<img src="${escapeHTML(item.image)}" alt="${title}">`
            : `<div class="cart-drawer__item-placeholder"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></div>`;

          
          // Check for sale pricing
          const hasCompare = item.original_line_price && item.original_line_price > item.final_line_price;
          const price = formatMoney(item.final_line_price);
          const comparePrice = hasCompare ? formatMoney(item.original_line_price) : '';
          
          const qty = item.quantity;
          const variantId = item.variant_id ? Number(item.variant_id) : 0;
          const itemUrl = item.url || item.product_url || '';
          const productHandle = item.handle || (itemUrl.match(/\/products\/([^?/]+)/) || [])[1] || '';

          return `
            <div class="cart-drawer__item"
              data-cart-item
              data-item-key="${escapeHTML(item.key)}"
              data-variant-id="${variantId}"
              data-product-handle="${escapeHTML(productHandle)}"
              data-current-qty="${qty}">
              <div class="cart-drawer__item-image">${img}</div>

              <div class="cart-drawer__item-meta">
                <p class="cart-drawer__item-title">${title}</p>
                ${variant ? `<p class="cart-drawer__item-variant">${variant}</p>` : ''}
                <div class="cart-drawer__item-pricing">
                  ${hasCompare ? `<span class="price__badge price__badge--sale">Sale</span>` : ''}
                  ${hasCompare ? `<s class="price__compare">${comparePrice}</s>` : ''}
                  <span class="cart-drawer__item-price${hasCompare ? ' price--on-sale' : ''}">${price}</span>
                </div>

<div class="cart-drawer__item-row">
  <div class="cart-drawer__qty-wrap">
    <div class="cart-drawer__qty" role="group" aria-label="Quantity">
      <button type="button" aria-label="Decrease quantity" data-qty-minus>-</button>

      <input
        type="number"
        min="0"
        step="1"
        inputmode="numeric"
        pattern="[0-9]*"
        value="${qty}"
        data-qty-input
        aria-label="Quantity"
      >

      <button type="button" aria-label="Increase quantity" data-qty-plus>+</button>
    </div>

    <div class="cart-drawer__item-actions" aria-label="Item actions">
      <button
        class="cart-drawer__icon-btn"
        type="button"
        aria-label="Edit item"
        data-edit
        data-item-url="${escapeHTML(itemUrl)}"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
        </svg>
      </button>

      <button
        class="cart-drawer__icon-btn"
        type="button"
        aria-label="Remove item"
        data-remove
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
        </svg>
      </button>
    </div>
  </div>

  <div class="cart-drawer__price" aria-label="Line price">${price}</div>
</div>

                <!-- We animate with aria-hidden + data-visible -->
                <p class="cart-drawer__item-error" data-item-error aria-hidden="true"></p>
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

    function setHeaderCount(cart) {
      const els = document.querySelectorAll(selectors.headerCount);
      const count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;

      els.forEach((el) => {
        if (count > 0) {
          el.textContent = String(count);
          el.removeAttribute('hidden');
        } else {
          el.textContent = '';
          el.setAttribute('hidden', 'hidden');
        }
      });
    }

  function setSubtotal(cart, subtotalEl) {
    if (!subtotalEl) return;
    const cents = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
    subtotalEl.textContent = formatMoney(cents);
  }

  function announce(liveEl, message) {
    if (!liveEl) return;
    liveEl.textContent = '';
    setTimeout(() => { liveEl.textContent = message; }, 20);
  }

  function showEl(el) { if (el) el.hidden = false; }
  function hideEl(el) { if (el) el.hidden = true; }

  function trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  async function updateFreeShippingUI(drawerEl, cart) {
    const wrap = drawerEl.querySelector(selectors.freeWrap);
    const textEl = drawerEl.querySelector(selectors.freeText);
    const fillEl = drawerEl.querySelector(selectors.freeFill);
    if (!wrap || !textEl || !fillEl) return;

    const enabled = drawerEl.getAttribute('data-free-shipping-enabled') === 'true';
    if (!enabled) { hideEl(wrap); return; }

    const eligibleCountry = (drawerEl.getAttribute('data-free-shipping-country') || 'GB').toUpperCase();
    const customerCountry = (drawerEl.getAttribute('data-customer-country') || '').toUpperCase();

    if (!customerCountry || customerCountry !== eligibleCountry) {
      hideEl(wrap);
      return;
    }

    const threshold = parseInt(drawerEl.getAttribute('data-free-shipping-threshold-cents') || '0', 10);
    if (!Number.isFinite(threshold) || threshold <= 0) { hideEl(wrap); return; }

    showEl(wrap);

    // Eligible subtotal excludes sale items (variant.compare_at_price > price) —
    // free shipping does not apply to discounted/sale stock. /cart.js does not
    // include compare_at_price, so we look it up via the variant cache.
    let subtotal = 0;
    if (cart && Array.isArray(cart.items)) {
      const variantData = await Promise.all(cart.items.map((it) => {
        const vid = it && Number(it.variant_id);
        return vid ? fetchVariantInventory(vid).catch(() => null) : Promise.resolve(null);
      }));
      cart.items.forEach((item, idx) => {
        if (!item || typeof item.final_line_price !== 'number') return;
        const v = variantData[idx];
        const cmp = v && v.compare_at_price;
        const vPrice = v && v.price;
        const onSale = cmp && vPrice && cmp > vPrice;
        if (!onSale) subtotal += item.final_line_price;
      });
    }
    const bar = wrap.querySelector('.cart-drawer__free-shipping-bar');

    if (subtotal >= threshold) {
      textEl.innerHTML = `You’ve unlocked <strong>FREE shipping</strong>.`;
      fillEl.style.width = '100%';
      bar?.setAttribute('aria-valuenow', '100');
      return;
    }

    const remaining = threshold - subtotal;
    const pct = Math.max(0, Math.min(100, Math.round((subtotal / threshold) * 100)));

    textEl.innerHTML = `You’re ${escapeHTML(formatMoney(remaining))} away from <strong>FREE shipping</strong>.`;
    fillEl.style.width = pct + '%';
    bar?.setAttribute('aria-valuenow', String(pct));
  }

  /* ── Cart Upsell Logic ── */
  function renderCartUpsell(drawer, cart) {
    const upsellEl = drawer.querySelector('[data-cart-upsell]');
    if (!upsellEl) return;

    if (!cart || !cart.items || cart.items.length === 0) {
      upsellEl.hidden = true;
      upsellEl.innerHTML = '';
      return;
    }

    const tpl = drawer.querySelector('[data-cart-upsell-template]');
    if (!tpl) { upsellEl.hidden = true; return; }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = tpl.innerHTML;

    const blocks = tempDiv.querySelectorAll('.cart-upsell-data');
    if (!blocks.length) { upsellEl.hidden = true; return; }

    // Collect cart item handles and product IDs for matching/exclusion
    const cartProductIds = new Set(cart.items.map(i => String(i.product_id)));
    const cartHandles = cart.items.map(i =>
      i.handle || ((i.url || '').match(/\/products\/([^?/]+)/) || [])[1] || ''
    ).filter(Boolean);

    let matchedHeading = '';
    const matchedProducts = [];
    let maxUpsells = 1;

    for (const block of blocks) {
      if (matchedProducts.length >= maxUpsells) break;

      // Check if any cart item handle is in this block's trigger collection
      const triggerHandlesStr = block.getAttribute('data-trigger-handles') || '';
      if (!triggerHandlesStr) continue;

      const triggerHandleSet = new Set(triggerHandlesStr.split(','));
      const matchingCartHandles = cartHandles.filter(h => triggerHandleSet.has(h));
      if (!matchingCartHandles.length) continue;

      // Build handle→vendor map from trigger collection data
      const vendorMapStr = block.getAttribute('data-trigger-vendors') || '';
      const handleToVendor = new Map();
      if (vendorMapStr) {
        vendorMapStr.split('|').forEach(entry => {
          const idx = entry.indexOf(':');
          if (idx > 0) handleToVendor.set(entry.slice(0, idx), entry.slice(idx + 1));
        });
      }

      // Collect vendors of matching cart items
      const cartVendors = new Set();
      for (const h of matchingCartHandles) {
        const v = handleToVendor.get(h);
        if (v) cartVendors.add(v);
      }

      if (!matchedHeading) matchedHeading = block.getAttribute('data-heading') || 'Goes great with your order';
      maxUpsells = parseInt(block.getAttribute('data-upsell-count') || '1', 10) || 1;
      const products = block.querySelectorAll('.cart-upsell-product');

      // Skip this block if the cart already contains a product from the upsell collection
      const upsellProductIds = new Set();
      for (const prod of products) {
        upsellProductIds.add(prod.getAttribute('data-product-id'));
      }
      const alreadyHasUpsell = cart.items.some(i => upsellProductIds.has(String(i.product_id)));
      if (alreadyHasUpsell) continue;

      // Extract colours from matching cart items
      const cartColours = new Set();
      for (const item of cart.items) {
        const handle = item.handle || ((item.url || '').match(/\/products\/([^?/]+)/) || [])[1] || '';
        if (!handle || !triggerHandleSet.has(handle)) continue;

        let foundColour = false;
        if (item.options_with_values) {
          for (const opt of item.options_with_values) {
            const name = (opt.name || '').toLowerCase();
            if (name.includes('color') || name.includes('colour')) {
              cartColours.add((opt.value || '').toLowerCase().trim());
              foundColour = true;
            }
          }
        }
        if (!foundColour && item.variant_title) {
          const parts = item.variant_title.split('/').map(p => p.trim().toLowerCase());
          if (parts.length > 0 && parts[0]) cartColours.add(parts[0]);
        }
      }

      // Collect eligible products (not in cart, not already matched)
      const alreadyMatchedIds = new Set(matchedProducts.map(p => p.getAttribute('data-product-id')));
      const eligible = [];
      for (const prod of products) {
        const prodId = prod.getAttribute('data-product-id');
        if (cartProductIds.has(prodId)) continue;
        if (alreadyMatchedIds.has(prodId)) continue;
        eligible.push(prod);
      }

      // Sort eligible: brand+colour first, then brand only, then others
      const sorted = [];
      const brandColour = [];
      const brandOnly = [];
      const rest = [];

      for (const prod of eligible) {
        const prodVendor = (prod.getAttribute('data-product-vendor') || '').toLowerCase();
        const isBrandMatch = prodVendor && cartVendors.has(prodVendor);

        if (isBrandMatch && cartColours.size > 0) {
          const prodColours = (prod.getAttribute('data-product-colours') || '').split(',').map(c => c.trim()).filter(Boolean);
          if (prodColours.some(c => cartColours.has(c))) {
            brandColour.push(prod);
            continue;
          }
        }
        if (isBrandMatch) { brandOnly.push(prod); continue; }
        rest.push(prod);
      }

      sorted.push(...brandColour, ...brandOnly, ...rest);

      const remaining = maxUpsells - matchedProducts.length;
      for (let i = 0; i < Math.min(remaining, sorted.length); i++) {
        matchedProducts.push(sorted[i]);
      }
    }

    if (!matchedProducts.length) {
      upsellEl.hidden = true;
      upsellEl.innerHTML = '';
      return;
    }

    function renderUpsellCard(prod) {
      const title = prod.getAttribute('data-product-title');
      const price = parseInt(prod.getAttribute('data-product-price') || '0', 10);
      const comparePrice = parseInt(prod.getAttribute('data-product-compare-price') || '0', 10);
      const image = prod.getAttribute('data-product-image');
      const url = prod.getAttribute('data-product-url');
      const variantId = prod.getAttribute('data-variant-id');
      const needsSelection = prod.getAttribute('data-needs-selection') === 'true';

      const hasCompare = comparePrice && comparePrice > price;
      const priceHTML = hasCompare
        ? `<s class="price__compare">${formatMoney(comparePrice)}</s> <span class="price--on-sale">${formatMoney(price)}</span>`
        : `<span>${formatMoney(price)}</span>`;

      return `
        <div class="cart-drawer__upsell-card">
          ${image ? `<a href="${escapeHTML(url)}" class="cart-drawer__upsell-image"><img src="${escapeHTML(image)}" alt="${escapeHTML(title)}" loading="lazy"></a>` : ''}
          <div class="cart-drawer__upsell-info">
            <a href="${escapeHTML(url)}" class="cart-drawer__upsell-title">${escapeHTML(title)}</a>
            <div class="cart-drawer__upsell-price">${priceHTML}</div>
          </div>
          ${needsSelection
            ? `<a href="${escapeHTML(url)}" class="cart-drawer__upsell-btn">View</a>`
            : `<button type="button" class="cart-drawer__upsell-btn" data-upsell-add data-upsell-variant-id="${escapeHTML(variantId)}">Add</button>`
          }
        </div>`;
    }

    upsellEl.innerHTML = `
      <div class="cart-drawer__upsell-inner">
        <p class="cart-drawer__upsell-heading">${escapeHTML(matchedHeading)}</p>
        ${matchedProducts.map(renderUpsellCard).join('')}
      </div>
    `;
    upsellEl.hidden = false;
  }

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = document.querySelector(selectors.drawer);
    const overlay = document.querySelector(selectors.overlay);
    const closeBtn = document.querySelector(selectors.closeBtn);
    const loadingEl = document.querySelector(selectors.loading);
    const content = document.querySelector(selectors.content);
    const subtotalEl = document.querySelector(selectors.subtotal);
    const messageEl = document.querySelector(selectors.error);
    const liveEl = document.querySelector(selectors.live);

    if (!drawer || !overlay || !content) return;

  function injectEmptyRecsIfNeeded(cart) {
    if (!cart || !cart.items || cart.items.length !== 0) return;

    const placeholder = content.querySelector('[data-empty-recs]');
    if (!placeholder) return;

    const tpl = drawer.querySelector('[data-cart-empty-recs-template]');
    if (!tpl) return;

    placeholder.innerHTML = tpl.innerHTML;
  }

    let lastActiveElement = null;
    let bodyOverflowBefore = '';
    let isOpen = false;
    let refreshPromise = null;

    let globalClearTimer = null;
    const itemMsgTimers = new Map();
    const commitTimers = new Map();

    // ✅ Prevent “flash / duplicated-looking lines”
    function setLoadingState(isLoading, useSkeleton) {
      if (useSkeleton && loadingEl) showEl(loadingEl);
      else if (loadingEl) hideEl(loadingEl);

      if (useSkeleton) {
        drawer.setAttribute('data-loading', 'true');
        content.style.opacity = '0';
        content.style.transition = 'opacity 160ms ease';
      } else {
        drawer.removeAttribute('data-loading');
        content.style.opacity = '1';
      }
    }

    function showGlobalMessage(msg, { autoClearMs = 6000 } = {}) {
      if (!messageEl) return;

      if (globalClearTimer) {
        clearTimeout(globalClearTimer);
        globalClearTimer = null;
      }

      if (!msg) {
        messageEl.hidden = true;
        messageEl.textContent = '';
        return;
      }

      messageEl.hidden = false;
      messageEl.textContent = msg;

      if (autoClearMs) {
        globalClearTimer = setTimeout(() => {
          messageEl.hidden = true;
          messageEl.textContent = '';
          globalClearTimer = null;
        }, autoClearMs);
      }
    }

    function clearItemMessage(itemEl) {
      if (!itemEl) return;
      const el = itemEl.querySelector(selectors.itemError);
      if (!el) return;

      el.setAttribute('aria-hidden', 'true');
      el.removeAttribute('data-visible');

      setTimeout(() => {
        if (el.getAttribute('aria-hidden') === 'true') el.textContent = '';
      }, 220);
    }

    function showItemMessage(itemEl, msg, { autoClearMs = 5000 } = {}) {
      if (!itemEl) return;
      const key = itemEl.getAttribute('data-item-key') || '';
      const el = itemEl.querySelector(selectors.itemError);
      if (!el) return;

      if (itemMsgTimers.has(key)) {
        clearTimeout(itemMsgTimers.get(key));
        itemMsgTimers.delete(key);
      }

      if (!msg) {
        clearItemMessage(itemEl);
        return;
      }

      el.textContent = msg;

      requestAnimationFrame(() => {
        el.setAttribute('aria-hidden', 'false');
        el.setAttribute('data-visible', 'true');
      });

      if (autoClearMs) {
        const t = setTimeout(() => {
          const fresh = content.querySelector(`[data-cart-item][data-item-key="${CSS.escape(key)}"]`);
          if (fresh) clearItemMessage(fresh);
          itemMsgTimers.delete(key);
        }, autoClearMs);

        itemMsgTimers.set(key, t);
      }
    }

    async function hydrateInputMaxes() {
      const itemEls = content.querySelectorAll(selectors.item);
      for (const itemEl of itemEls) {
        const variantId = Number(itemEl.getAttribute('data-variant-id') || 0);
        const input = itemEl.querySelector('[data-qty-input]');
        if (!variantId || !input) continue;

        const variantData = await fetchVariantInventory(variantId);
        const max = variantData?.inventory_quantity;
        if (typeof max === 'number') {
          input.max = String(max);
          itemEl.setAttribute('data-max', String(max));
        } else {
          input.removeAttribute('max');
          itemEl.removeAttribute('data-max');
        }
      }
    }

    async function hydrateSaleBadges() {
      const itemEls = content.querySelectorAll(selectors.item);
      for (const itemEl of itemEls) {
        const variantId = Number(itemEl.getAttribute('data-variant-id') || 0);
        const pricingDiv = itemEl.querySelector('.cart-drawer__item-pricing');

        // Add sale badge from variant data if not already present
        if (variantId && pricingDiv) {
          const hasBadge = pricingDiv.querySelector('.price__badge--sale') || pricingDiv.querySelector('.price__badge--final-sale');
          if (!hasBadge) {
            const variantData = await fetchVariantInventory(variantId);
            const compareAtPrice = variantData?.compare_at_price;
            const price = variantData?.price;

            if (compareAtPrice && price && compareAtPrice > price) {
              const priceEl = pricingDiv.querySelector('.cart-drawer__item-price');
              if (priceEl) {
                priceEl.classList.add('price--on-sale');

                const badge = document.createElement('span');
                badge.className = 'price__badge price__badge--sale';
                badge.textContent = 'Sale';

                const compareEl = document.createElement('s');
                compareEl.className = 'price__compare';
                compareEl.textContent = formatMoney(compareAtPrice);

                pricingDiv.insertBefore(badge, priceEl);
                pricingDiv.insertBefore(compareEl, priceEl);
              }
            }
          }
        }

        // Check product tags for final_sale / new and set appropriate badge
        const handle = itemEl.getAttribute('data-product-handle');
        if (handle && pricingDiv) {
          const productData = await fetchProductData(handle);
          if (productData && productData.tags) {
            const tags = (typeof productData.tags === 'string')
              ? productData.tags.split(',').map(t => t.toLowerCase().trim())
              : Array.isArray(productData.tags)
                ? productData.tags.map(t => String(t).toLowerCase().trim())
                : [];

            if (tags.includes('final_sale') || tags.includes('final-sale') || tags.includes('finalsale')) {
              const badge = pricingDiv.querySelector('.price__badge--sale');
              if (badge) {
                badge.className = 'price__badge price__badge--final-sale';
                badge.textContent = 'Final Sale';
              }
            } else if (!pricingDiv.querySelector('.price__badge')) {
              // No sale/final-sale badge — check for New
              var isNew = tags.includes('new');
              if (!isNew && productData.published_at) {
                var published = new Date(productData.published_at).getTime();
                var oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
                var cutoff = new Date('2026-02-17').getTime();
                if (published > oneWeekAgo && published >= cutoff) isNew = true;
              }
              if (tags.includes('not-new') || tags.includes('not_new')) {
                isNew = false;
              }
              if (isNew) {
                var newBadge = document.createElement('span');
                newBadge.className = 'price__badge price__badge--new';
                newBadge.textContent = 'New';
                pricingDiv.insertBefore(newBadge, pricingDiv.firstChild);
              }
            }

            // Online Exclusive (Gardiners direct-ship range) — shows in addition to any other badge
            if (tags.includes('gardiners') && !pricingDiv.querySelector('.price__badge--online-exclusive')) {
              var exclusiveBadge = document.createElement('span');
              exclusiveBadge.className = 'price__badge price__badge--online-exclusive';
              exclusiveBadge.textContent = 'Online Exclusive';
              var existingBadge = pricingDiv.querySelector('.price__badge');
              if (existingBadge) {
                existingBadge.insertAdjacentElement('afterend', exclusiveBadge);
              } else {
                pricingDiv.insertBefore(exclusiveBadge, pricingDiv.firstChild);
              }
            }

            // Gardiners direct-ship dispatch note — longer delivery reminder
            if (tags.includes('gardiners')) {
              var dispatchMeta = itemEl.querySelector('.cart-drawer__item-meta');
              if (dispatchMeta && !dispatchMeta.querySelector('.cart-drawer__item-dispatch')) {
                var drawerEl = itemEl.closest('[data-cart-drawer]');
                var dispatchMsg = (drawerEl && drawerEl.getAttribute('data-gardiners-dispatch-message')) || '';
                if (dispatchMsg) {
                  var dispatchNote = document.createElement('p');
                  dispatchNote.className = 'cart-drawer__item-dispatch';
                  dispatchNote.textContent = dispatchMsg;
                  pricingDiv.insertAdjacentElement('afterend', dispatchNote);
                }
              }
            }
          }
        }
      }
    }

    function getMaxFromItemEl(itemEl) {
      const raw = itemEl.getAttribute('data-max');
      if (raw == null || raw === '') return null;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : null;
    }

    async function ensureMaxLoaded(itemEl) {
      let max = getMaxFromItemEl(itemEl);
      if (max !== null) return max;

      const variantId = Number(itemEl.getAttribute('data-variant-id') || 0);
      if (!variantId) return null;

      const variantData = await fetchVariantInventory(variantId);
      max = variantData?.inventory_quantity;

      const input = itemEl.querySelector('[data-qty-input]');
      if (input && typeof max === 'number') {
        input.max = String(max);
        itemEl.setAttribute('data-max', String(max));
      }

      return (typeof max === 'number') ? max : null;
    }

    function setLineBusy(itemEl, busy) {
      itemEl.classList.toggle('is-updating', !!busy);
      const controls = itemEl.querySelectorAll('[data-qty-minus],[data-qty-plus],[data-qty-input],[data-remove],[data-edit]');
      controls.forEach((c) => {
        if (busy) c.setAttribute('disabled', 'disabled');
        else c.removeAttribute('disabled');
      });
    }

    // ✅ 422 handler: show message AND unlock the row (no refresh, cart didn’t change)
    function handleStockLimit(itemEl, err) {
      const msg = stockMessageFromShopify422(err);

      // unlock the row so the cart doesn't "freeze"
      if (itemEl) setLineBusy(itemEl, false);

      showItemMessage(itemEl, msg, { autoClearMs: 5500 });
      announce(liveEl, msg);
    }

    async function refresh({ skeleton } = {}) {
      if (refreshPromise) return refreshPromise;

      setLoadingState(true, !!skeleton);

      refreshPromise = (async () => {
        try {
          const cart = await getCart();

          content.innerHTML = renderItems(cart);
          injectEmptyRecsIfNeeded(cart);

        // One-time cart page sync (prevents infinite reload loop)
        if (
          window.location.pathname === '/cart' &&
          sessionStorage.getItem('cart_drawer_needs_cart_refresh') === '1'
        ) {
          sessionStorage.removeItem('cart_drawer_needs_cart_refresh');
          window.location.reload();
          return cart;
        }

          drawer.setAttribute('data-cart-empty', (!cart.items || cart.items.length === 0) ? 'true' : 'false');

          setSubtotal(cart, subtotalEl);
          setHeaderCount(cart);

          updateFreeShippingUI(drawer, cart);
          renderCartUpsell(drawer, cart);

          await hydrateInputMaxes();
          await hydrateSaleBadges();

          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

          setLoadingState(false, false);
          return cart;
        } catch (err) {
          setLoadingState(false, false);
          showGlobalMessage('Sorry, we couldn’t update your bag. Please try again.', { autoClearMs: 6000 });
          updateFreeShippingUI(drawer, null);
          throw err;
        } finally {
          refreshPromise = null;
        }
      })();

      return refreshPromise;
    }

    function open() {
    if (isOpen) return;
    isOpen = true;

    lastActiveElement = document.activeElement;

    overlay.hidden = false;
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.style.visibility = '';
    document.body.classList.add('cart-drawer-open');

    bodyOverflowBefore = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // ✅ Do NOT show skeletons on open
    setLoadingState(false, false);

    // ✅ Just refresh silently
    refresh({ skeleton: false }).catch(() => {});

    setTimeout(() => drawer.focus(), 0);
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;

      document.body.classList.remove('cart-drawer-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      setTimeout(() => { overlay.hidden = true; }, 250);

      document.body.style.overflow = bodyOverflowBefore || '';
      showGlobalMessage('');
      if (loadingEl) hideEl(loadingEl);

      setLoadingState(false, false);

      if (lastActiveElement && lastActiveElement.focus) lastActiveElement.focus();
    }

    document.addEventListener('click', (e) => {
      const opener = e.target.closest(selectors.openers);
      if (!opener) return;
      e.preventDefault();
      open();
    });

    drawer.addEventListener('click', (e) => {
      if (!e.target.closest('[data-close-cart]')) return;
      e.preventDefault();
      close();
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (!document.body.classList.contains('cart-drawer-open')) return;
      if (e.key === 'Escape') close();
      trapFocus(drawer, e);
    });

    function scheduleCommit(itemEl, key, desired) {
      if (commitTimers.has(key)) {
        clearTimeout(commitTimers.get(key));
        commitTimers.delete(key);
      }

      const t = setTimeout(async () => {
        commitTimers.delete(key);

        try {
          setLineBusy(itemEl, true);
          clearItemMessage(itemEl);
          showGlobalMessage('');

          await changeLineByKey(key, desired);
          await refresh({ skeleton: false });
          announce(liveEl, 'Updated.');
        } catch (err) {
          if (err && err.status === 422) {
            handleStockLimit(itemEl, err); // ✅ also unlocks row
          } else {
            const msg = 'Sorry, we couldn’t update your bag. Please try again.';
            showGlobalMessage(msg, { autoClearMs: 6000 });
            announce(liveEl, msg);
            await refresh({ skeleton: false });
          }
        }
      }, 250);

      commitTimers.set(key, t);
    }

    drawer.addEventListener('wheel', (e) => {
      const input = e.target.closest('[data-qty-input]');
      if (!input) return;
      if (document.activeElement === input) e.preventDefault();
    }, { passive: false });

    drawer.addEventListener('keydown', (e) => {
      const input = e.target.closest('[data-qty-input]');
      if (!input) return;
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
    });

    drawer.addEventListener('click', async (e) => {
      const itemEl = e.target.closest(selectors.item);
      if (!itemEl) return;

      const key = itemEl.getAttribute('data-item-key');
      const input = itemEl.querySelector('[data-qty-input]');
      const currentQty = parseInt(itemEl.getAttribute('data-current-qty') || '0', 10);
      const currentVal = input ? parseInt(input.value || '0', 10) : currentQty;

      if (commitTimers.has(key)) {
        clearTimeout(commitTimers.get(key));
        commitTimers.delete(key);
      }

      try {
          if (e.target.matches('[data-qty-minus]') || e.target.closest('[data-qty-minus]')) {
          const desired = Math.max(0, currentVal - 1);
          setLineBusy(itemEl, true);
          clearItemMessage(itemEl);
          showGlobalMessage('');

          await changeLineByKey(key, desired);
          await refresh({ skeleton: false });
          announce(liveEl, 'Updated.');
          return;
        }

          if (e.target.matches('[data-qty-plus]') || e.target.closest('[data-qty-plus]')) {

          const max = await ensureMaxLoaded(itemEl);
          const desiredRaw = currentVal + 1;

          if (typeof max === 'number' && desiredRaw > max) {
            if (input) input.value = String(max);
            const msg = stockMessageFromMax(max);
            showItemMessage(itemEl, msg, { autoClearMs: 5500 });
            announce(liveEl, msg);
            return;
          }

          setLineBusy(itemEl, true);
          clearItemMessage(itemEl);
          showGlobalMessage('');

          await changeLineByKey(key, desiredRaw);
          await refresh({ skeleton: false });
          announce(liveEl, 'Updated.');
          return;
        }

        if (e.target.matches('[data-edit]') || e.target.closest('[data-edit]')) {
          const btn = e.target.closest('[data-edit]');
          const url = btn ? (btn.getAttribute('data-item-url') || '') : '';

          setLineBusy(itemEl, true);
          clearItemMessage(itemEl);
          showGlobalMessage('');

          await changeLineByKey(key, 0);

          // Redirect back to the product page to re-select size/colour
          window.location.href = url || '/';
          return;
        }

      if (e.target.matches('[data-remove]') || e.target.closest('[data-remove]')) {
        setLineBusy(itemEl, true);
        clearItemMessage(itemEl);
        showGlobalMessage('');

        await changeLineByKey(key, 0);
        await refresh({ skeleton: false });
        announce(liveEl, 'Removed.');
        return;
      }
      } catch (err) {
        if (err && err.status === 422) {
          handleStockLimit(itemEl, err); // ✅ unlocks row now
        } else {
          const msg = 'Sorry, we couldn’t update your bag. Please try again.';
          showGlobalMessage(msg, { autoClearMs: 6000 });
          announce(liveEl, msg);
          await refresh({ skeleton: false });
        }
      } finally {
        // ✅ Safety: if an error happened before refresh re-render, ensure row isn't stuck disabled
        // (refresh will replace DOM anyway, so this is just a harmless extra guard)
        if (itemEl && itemEl.classList.contains('is-updating')) {
          // don't unlock if we successfully refreshed; but it's safe either way
          setLineBusy(itemEl, false);
        }
      }
    });

    drawer.addEventListener('input', async (e) => {
      const input = e.target.closest('[data-qty-input]');
      if (!input) return;

      const itemEl = input.closest(selectors.item);
      if (!itemEl) return;

      const max = await ensureMaxLoaded(itemEl);
      if (typeof max !== 'number') return;

      let desired = Math.max(0, parseInt(input.value || '0', 10));
      if (desired > max) {
        input.value = String(max);
        const msg = stockMessageFromMax(max);
        showItemMessage(itemEl, msg, { autoClearMs: 5500 });
        announce(liveEl, msg);
      }
    });

    drawer.addEventListener('change', async (e) => {
      const input = e.target.closest('[data-qty-input]');
      if (!input) return;

      const itemEl = input.closest(selectors.item);
      if (!itemEl) return;

      const key = itemEl.getAttribute('data-item-key');
      const currentQty = parseInt(itemEl.getAttribute('data-current-qty') || '0', 10);

      let desired = Math.max(0, parseInt(input.value || '0', 10));

      const max = await ensureMaxLoaded(itemEl);
      if (typeof max === 'number' && desired > max) {
        desired = max;
        input.value = String(max);
        const msg = stockMessageFromMax(max);
        showItemMessage(itemEl, msg, { autoClearMs: 5500 });
        announce(liveEl, msg);
      }

      if (desired === currentQty) return;

      scheduleCommit(itemEl, key, desired);
    });

    // Upsell "Add" button handler
    drawer.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-upsell-add]');
      if (!btn) return;
      e.preventDefault();

      const variantId = btn.getAttribute('data-upsell-variant-id');
      if (!variantId) return;

      btn.disabled = true;
      btn.textContent = 'Adding...';

      try {
        await addToCart({ items: [{ id: Number(variantId), quantity: 1 }] });
        await refresh({ skeleton: false });
        announce(liveEl, 'Added to bag.');
        if (window.mss && window.mss.track) {
          window.mss.track('upsell_pair_add', {
            pair_variant_id: variantId,
            pair_title: (btn.closest('.cart-drawer__upsell-card') || btn).querySelector('.cart-drawer__upsell-title')?.textContent?.trim() || ''
          });
        }
      } catch (err) {
        const msg = (err && err.userMessage) ? err.userMessage : 'Sorry, we couldn\'t add that item.';
        showGlobalMessage(msg, { autoClearMs: 6000 });
        announce(liveEl, msg);
        btn.disabled = false;
        btn.textContent = 'Add';
      }
    });

    window.CartDrawer = { open, close, refresh };
    window.CartAPI = {
      getCart,
      addToCart: async (payload) => {
        try {
          showGlobalMessage('');
          const res = await addToCart(payload);
          open();
          await refresh({ skeleton: false });
          announce(liveEl, 'Added to bag.');
          return res;
        } catch (err) {
          const msg = (err && err.userMessage) ? err.userMessage : 'Sorry, we couldn’t add that item.';
          showGlobalMessage(msg, { autoClearMs: 6000 });
          announce(liveEl, msg);
          throw err;
        }
      }
    };

    updateFreeShippingUI(drawer, null);

    refresh({ skeleton: false }).catch(() => {});
  });
})();
